import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Request,
  Response,
  TQueryParams,
  formatQueryParams,
  formatRequest,
  parseJsonApiResponse,
  prepareHeaders,
} from 'evolve-ui';
import { TOrganizationalEntity } from 'evolve-ui/src/common/types/organizational-entity';

export enum AnadoluContactGroupStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export type AnadoluContactGroupContact = {
  id?: string;
  contactId?: number;
  name?: string;
  phone?: string;
  email?: string;
  data?: {
    customer?: {
      id?: number;
      name?: string;
      number?: number;
      customerStatusId?: number;
      fiscalNumber?: string;
    };
  };
};

export interface TAnadoluContactGroup {
  id?: string;
  type?: 'contact-group';
  name?: string;
  contacts?: AnadoluContactGroupContact[];
  status?: AnadoluContactGroupStatus;
  emails?: number;
  phones?: number;
  agency?: TOrganizationalEntity;
  createdAt?: number;
  updatedAt?: number;
}

const baseUrl = `${process.env.GATSBY_CUSTOMERS_API_URL}/anadolu/contact-groups`;

export const anadoluContactGroupsApi = createApi({
  reducerPath: 'anadolu-contact-groups',
  baseQuery: fetchBaseQuery({
    baseUrl,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['AnadoluContactGroup', 'AnadoluContactGroupContacts'],
  endpoints: (builder) => ({
    getAnadoluContactGroups: builder.query<
      Response<TAnadoluContactGroup>,
      TQueryParams
    >({
      providesTags: ['AnadoluContactGroup'],
      query: formatQueryParams,
      transformResponse: parseJsonApiResponse<TAnadoluContactGroup>,
    }),

    getAnadoluContactGroup: builder.query<
      Response<TAnadoluContactGroup>,
      { contactGroupId: string }
    >({
      providesTags: ['AnadoluContactGroup'],
      query: ({ contactGroupId }) =>
        formatQueryParams(undefined, contactGroupId),
      transformResponse: parseJsonApiResponse<TAnadoluContactGroup>,
    }),

    getAnadoluContactGroupsContacts: builder.query<
      Response<AnadoluContactGroupContact>,
      TQueryParams & { contactGroupId: string }
    >({
      providesTags: ['AnadoluContactGroupContacts'],
      query: ({ contactGroupId, ...rest }) =>
        formatQueryParams(rest, `${contactGroupId}/contacts`),
      transformResponse: parseJsonApiResponse<AnadoluContactGroupContact>,
    }),

    postAnadoluContactGroup: builder.mutation<
      Response<TAnadoluContactGroup>,
      Omit<TAnadoluContactGroup, 'id' | 'phones' | 'emails'>
    >({
      invalidatesTags: ['AnadoluContactGroup'],
      query: (params) =>
        formatRequest({ ...params, type: 'contact-group' }, Request.POST, ''),
      transformResponse: parseJsonApiResponse<TAnadoluContactGroup>,
    }),

    postAnadoluContactGroupContacts: builder.mutation<
      Response<TAnadoluContactGroup>,
      { contactGroupId: string; contactIds: string[] }
    >({
      invalidatesTags: ['AnadoluContactGroup', 'AnadoluContactGroupContacts'],
      query: ({ contactGroupId, contactIds }) => ({
        url: `${contactGroupId}/contacts`,
        body: {
          data: contactIds.map((id) => ({
            attributes: { contactId: id },
            type: 'contacts',
          })),
        },
        method: Request.POST,
      }),
      transformResponse: parseJsonApiResponse<TAnadoluContactGroup>,
    }),

    deleteAnadoluContactGroups: builder.mutation<
      Response<TAnadoluContactGroup>,
      { id: string }
    >({
      invalidatesTags: ['AnadoluContactGroup'],
      query: ({ id: contactGroupId, ...params }) =>
        formatRequest(
          { ...params, type: 'contact-group' },
          Request.DELETE,
          contactGroupId,
        ),
      transformResponse: parseJsonApiResponse<TAnadoluContactGroup>,
    }),

    deleteAnadoluContactGroupContacts: builder.mutation<
      Response<TAnadoluContactGroup>,
      { contactGroupId: string; contactIds: string[] }
    >({
      invalidatesTags: ['AnadoluContactGroupContacts'],
      query: ({ contactGroupId, contactIds }) => ({
        url: `${contactGroupId}/contacts`,
        body: {
          data: contactIds.map((id) => ({ id, type: 'contacts' })),
        },
        method: Request.DELETE,
      }),
      transformResponse: parseJsonApiResponse<TAnadoluContactGroup>,
    }),

    patchAnadoluContactGroups: builder.mutation<
      Response<TAnadoluContactGroup>,
      { contactGroupId: string; update: Partial<TAnadoluContactGroup> }
    >({
      invalidatesTags: ['AnadoluContactGroup'],
      query: ({ contactGroupId, update }) =>
        formatRequest(
          { ...update, type: 'contact-group' },
          Request.PATCH,
          contactGroupId,
        ),
      transformResponse: parseJsonApiResponse<TAnadoluContactGroup>,
    }),

    patchAnadoluContactGroupContact: builder.mutation<
      Response<AnadoluContactGroupContact>,
      {
        contactGroupId: string;
        contactGroupContactId: string;
        update: Partial<AnadoluContactGroupContact>;
      }
    >({
      invalidatesTags: ['AnadoluContactGroupContacts'],
      query: ({ contactGroupId, contactGroupContactId, update }) =>
        formatRequest(
          { ...update, type: 'contact-group' },
          Request.PATCH,
          `${contactGroupId}/contacts/${contactGroupContactId}`,
        ),
      transformResponse: parseJsonApiResponse<AnadoluContactGroupContact>,
    }),
  }),
});

export const {
  useGetAnadoluContactGroupsQuery,
  useGetAnadoluContactGroupQuery,
  useGetAnadoluContactGroupsContactsQuery,
  usePostAnadoluContactGroupMutation,
  usePostAnadoluContactGroupContactsMutation,
  usePatchAnadoluContactGroupsMutation,
  usePatchAnadoluContactGroupContactMutation,
  useDeleteAnadoluContactGroupsMutation,
  useDeleteAnadoluContactGroupContactsMutation,
} = anadoluContactGroupsApi;
